import CryptoJS from 'crypto-js';
import {
    SEPARATOR_SLASH,
    SEPARATOR_COLON,
    SEPARATOR_SEMICOLON,
    SEPARATOR_NEW_LINE,
    SIGN_PART_CREDENTIAL,
    SIGN_PART_SIGNED_HEADERS,
    SIGN_PART_SIGNATURE
} from '../../constants/AuthenticationTypeHMAC';

/**
 * Zwraca ustandaryzowaną ścieżkę zasobu
 * 
 * Jeśli ścieżka jest pusta - zwracamy ukośnik /
 * 
 * Jeśli ścieżka nie zaczyna się od ukośnika / - doklejamy ukośnik
 * 
 * @param {String} endpoint oryginalna ścieżka zasobu
 * @returns {String} postać ścieżki do podpisu
 */
export const getCanonicalizedResourcePath = (endpoint) => {

    //jesli niezdefiniowamy - zwracamy /
    if (!endpoint) {
        return SEPARATOR_SLASH;
    }

    //jesli pusty - zwracamy /
    if (endpoint.trim().length === 0) {
        return SEPARATOR_SLASH;
    }

    //jesli nie zaczyna sie od / - doklejamy na początku
    if (endpoint.startsWith(SEPARATOR_SLASH)) {
        return endpoint;
    } else {
        return `${SEPARATOR_SLASH}${endpoint}`
    }

}

/**
 * Zwraca kolekcje nazw nagłówków, biorących udział w podpisie
 * 
 * Nagłówki są przekonwertowane na małe litery i posortowane(ignorując wielkość liter).
 * 
 * Nagłówki są przedziolone separatorem średnik (na końcu nie ma średnika)
 * 
 * 
 * @param {Map} headerMap nagłówki HTTP - tylko te biorące udział w podpisie
 * @returns {String} postać nagłówków do podpisu np headerName1;headerName2;headerName3
 */
export const getCanonicalizeHeaderNames = (headerMap) => {

    //nagłowki w postaci nazwa_nagłówka1;nazwa_nagłówka2 - posortowane
    let result = '';

    // krok 1: sortujemy nagłówki bez względu na wielkość liter

    //sortowanie wg kluczy
    const sortStringKeys = (a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' });

    //posortowana mapa wg kluczy, ignorowanie wielkości liter
    const sortedMap = new Map([...headerMap].sort(sortStringKeys)) //headerMap.entries.sort(sortStringKeys);

    // krok 2: postać kanoniczna nagłówków: header1;header2;header3

    //przechodzimy przez elementy posortowanej mapy
    //klucze - lowercase
    sortedMap.forEach((value, key) => {
        //jeśli nie jest puste - doklejamy średnik
        if (result.trim().length !== 0) {
            result += SEPARATOR_SEMICOLON;
        }
        result += key.toLowerCase();
    })

    return result;
}

/**
* Tworzenie reprezentracji nagłówków w ustandaryzowanej formie - do podpisu.
* 
* 1. Nagłówki są przekonwertowane na małe litery
* 2. Nagłówki są posortowane po nazwie.
* 3. Wartości nagłówków mają usunięte początkowe i końcowe spacje.
* 
* CanonicalHeaders =
* CanonicalHeadersEntry0 + CanonicalHeadersEntry1 + ... + CanonicalHeadersEntryN
* CanonicalHeadersEntry =
* Lowercase(HeaderName) + ':' + Trimall(HeaderValue) + '\n'
* 
* Oryginalne nagłówki:
* My-header1:    a b c  \n
* X-Flow-Date:20150830T123600Z\n
* My-Header2:    "a b c"  \n
* 
* Wynik:
* my-header1:a b c\n
* my-header2:"a b c"\n
* x-flow-date:20150830T123600Z\n
* 
* @param {Map} headerMap nagłówki HTTP - tylko te biorące udział w podpisie
* @returns {String} postać nagłówków do podpisu
* 
*/
export const getCanonicalizedHeaderString = (headerMap) => {

    //nagłowki w postaci nazwa_nagłówka:wartość_nagłówka - posortowane
    let result = '';

    // krok 1: sortujemy nagłówki bez względu na wielkość liter

    //sortowanie wg kluczy
    const sortStringKeys = (a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' });

    //posortowana mapa wg kluczy, ignorowanie wielkości liter
    const sortedMap = new Map([...headerMap].sort(sortStringKeys)) //headerMap.entries.sort(sortStringKeys);

    // krok 2: postać kanoniczna nagłówków: header:value\n

    //przechodzimy przez elementy posortowanej mapy
    //klucze - lowercase
    //wartosc - uciete space z przodu i tyłu
    sortedMap.forEach((value, key) => {
        result += `${key.toLowerCase()}${SEPARATOR_COLON}${value.toString().trim()}${SEPARATOR_NEW_LINE}`;
    })

    return result;
}

/**
 * Tworzenie reprezentacji żądania(na podstawie prawdziwego żądania) w postaci stringu w określonym standardzie - do podpisu.
 * Aby zapewnić, że serwer otrzymujący żądanie, i klient tworzący żądanie - obliczą ten sam podpis - ważny jest każdy znak/ kolejność danych.
 *  
 * @param {String} endpointURL adres url, zawierający ścieżkę do dowolnego zasobu
 * @param {String} httpMethod metoda zapytania HTTP(get, put, post, delete...)
 * @param {Map} headerMap nagłówki HTTP - tylko te biorące udział w podpisie
 * @returns string ustandaryzowana reprezentacja prawdziwego żądania
 * 
 * CanonicalRequest =
 * HTTPRequestMethod + '\n' +
 * CanonicalURI + '\n' +
 * //query params - not supported in v1 - instead new line
 * CanonicalHeaders + '\n' +
 * //body - not supported in v1 - instead new line
 * 
 */
export const getCanonicalRequest = (endpointURL, httpMethod, headerMap) => {

    let result = '';
    // metoda - uppercase
    result += `${httpMethod.toUpperCase()}${SEPARATOR_NEW_LINE}`;
    // url
    result += `${getCanonicalizedResourcePath(endpointURL)}${SEPARATOR_NEW_LINE}`;
    // query params - not supported in v1 - instead new line
    result += SEPARATOR_NEW_LINE;
    // headers
    result += getCanonicalizedHeaderString(headerMap);
    // body - not supported in v1 - instead new line
    result += SEPARATOR_NEW_LINE;

    return result;

}

/** 
 * Oblicza podpis żądania 
 *
 * @param {String} endpointURL adres url, zawierający ścieżkę do dowolnego zasobu
 * @param {String} httpMethod metoda zapytania HTTP(get, put, post, delete...)
 * @param {Map} headerMap nagłówki HTTP - tylko te biorące udział w podpisie
 * @param {String} accessKey klucz dostępu, dzięki któremu identyfikujemy klienta
 * @param {String} secretKey sekretny klucz, którym podpisujemy żądanie
 * @returns {String} ustandaryzowana reprezentacja prawdziwego żądania
 */
export const computeSignature = (endpointURL, httpMethod, headerMap, accessKey, secretKey) => {

    // canonicalize the headers; we need the set of header names as well as the
    // names and values to go into the signature process
    const canonicalizedHeaderNames = getCanonicalizeHeaderNames(headerMap);

    // tworzymy postać żądania do podpisu
    const canonicalRequest = getCanonicalRequest(endpointURL, httpMethod, headerMap);

    //console.log("--------- Canonical request --------");
    //console.log(canonicalRequest);
    //console.log("------------------------------------");

    const signature = signHmacSHA256(canonicalRequest, secretKey);

    // console.log("------------- Signature ------------");
    // console.log(signature);
    // console.log("------------------------------------");

    const credentialsAuthorizationHeader = `${SIGN_PART_CREDENTIAL}=${accessKey}`;
    const signedHeadersAuthorizationHeader = `${SIGN_PART_SIGNED_HEADERS}=${canonicalizedHeaderNames}`;
    const signatureAuthorizationHeader = `${SIGN_PART_SIGNATURE}=${signature}`;

    const authorizationHeader = `${credentialsAuthorizationHeader}, ${signedHeadersAuthorizationHeader}, ${signatureAuthorizationHeader}`;

    // console.log("----------- Authorization ----------");
    // console.log(authorizationHeader);
    // console.log("------------------------------------");

    return authorizationHeader;

}

/**
 * Zwraca podpis wiadomości podanym kluczem
 * 
 * @param {*} stringToSign wiadomość do podpisania
 * @param {*} key klucz którym podpisujemy wiadomość
 * @returns podpis wiadomości podanym kluczem
 */
const signHmacSHA256 = (stringToSign, key) => {
    let hash = CryptoJS.HmacSHA256(stringToSign, key);
    // Encode in classical base64 with url standard
    let base64UrlSafe = base64UrlEncode(hash);
    return base64UrlSafe;
}

/**
 * Kodujemy base64 w stardardzie url
 * 
 * Czyli dodatkowo: 
 * 
 * usuwamy =
 * 
 * zamieniamy + na -
 * 
 * zamieniamy / na _
 * 
 * @param {WordArray} hash wiadomosc do zakodowania
 */
const base64UrlEncode = (hash) => {

    // Encode in classical base64
    let encodedSource = CryptoJS.enc.Base64.stringify(hash);

    // Remove padding equal characters
    encodedSource = encodedSource.replace(/=+$/, '');

    // Replace characters according to base64url specifications
    encodedSource = encodedSource.replace(/\+/g, '-');
    encodedSource = encodedSource.replace(/\//g, '_');

    return encodedSource;
}